/**
Copyright (C) 2019-2022, Infraclear Inc. All Rights Reserved.
*/

import React, { useState } from "react";
import "./setBarcode.scss";
import { ValidateGoogleTOTP } from "../../constants/api-urlConstants";
import { ErrorCodes } from "../../constants/messageCodes";
import { MessageText } from "../../constants/messageText";
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  TextField,
  Grid,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tooltip,
  styled,
  TooltipProps,
  tooltipClasses,
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import instance from "../../utils/axiosInstance";
import { SnackBarMessage } from "../snackBar/snackBar";
import { Authenticator } from "../authenticator/authenticator";
import { encryptpassword } from "../../constants/commonConstants";
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "rgb(46, 125, 50)",
    color: 'rgb(255, 255, 255)',
    boxShadow: theme.shadows[1],
    fontSize: 12,
  },
}));

export const SetBarcode: React.FC<any> = ({ qrCodeData, credentialsObjData, goToPrevious, showActivateForm }) => {

  const [open, setOpen] = React.useState(false);
  const [showAuthPopup, setShowAuthPopup] = React.useState(false);
  const [msgConfig, setMsgConfig] = useState({
    type: '',
    message: '',
    snackFlag: false
  })
  const [statusIsLoading, setStatusIsLoading] = React.useState(false);
  const initial_state_MFA = {
    code: ""
  }
  const MFASchema = Yup.object().shape({
    code: Yup.string().required(MessageText.VALIDATION_MFA_CODE_REQUIRED)
  });

  const activateMFALoader = (formObj: any) => {
    setStatusIsLoading(true)
    activateMFA(formObj);
  }

  const activateMFA = async (formObj: any) => {
    const USER_API_BASE_URL = `${ValidateGoogleTOTP}`;
    const encrypt_newPassword: any = await encryptpassword(credentialsObjData.password);
    const payload_MFA = {
      "email": credentialsObjData.email,
      "password": encrypt_newPassword,
      "code": formObj.code
    }
    instance
      .post(USER_API_BASE_URL, payload_MFA)
      .then((response) => {
        setStatusIsLoading(false);
        const res = response.data;
        if (res.success) {
          goToLogin(true);
        } else if (res.message_code == ErrorCodes.MFA_SECRET_EXPIRED) {
          redirectMFA_Expire(true);
        } else {
          if (res.message_code === ErrorCodes.INVALID_MFA_CODE || res.message_code === ErrorCodes.INVALID_SECRET_CODE
            || res.message_code === ErrorCodes.INVALID_SECRET
            || res.message_code === ErrorCodes.INVALID_PARAMETER
            || res.message_code === ErrorCodes.UNAUTHORIZED_ACTION
          ) {
            setMsgConfig({
              type: 'error',
              message: MessageText.INVALID_MFA_NO_MATCH,
              snackFlag: true
            })
          }
        }
      })
      .catch((error) => {
        setStatusIsLoading(false);
        setMsgConfig({
          type: 'error',
          message: MessageText.WENT_WRONG,
          snackFlag: true
        });
      });
  }

  const goToLogin = (value: any = false) => {
    const obj = {
      showMsg: value,
      hideChangepass: false
    }
    goToPrevious(obj)
  }

  const redirectMFA_Expire = (value: any = false) => {
    const obj = {
      showMsg: value,
      hideChangepass: false,
      MFA_EXPIRED: true
    }
    goToPrevious(obj)
  }

  const openInNewTab = (link: any) => {
    setShowAuthPopup(true);
  }

  const hideGenBarcode = () => {
    const obj = {
      showMsg: false,
      hideChangepass: false
    }
    goToPrevious(obj)
  }

  const handleCopyText = (e: any) => {
    navigator.clipboard.writeText(e)
    setOpen(true);
    setTimeout(() => {
      setOpen(false);
    }, 1000)
  }

  return (
    <div className="setBarcodeWrapper">
      <div className="MFAWrapper">
        <div className="headerWrapper">
          <span className="MFAtitle">{!showActivateForm && 'Set up '}{showActivateForm && 'Reset '}{MessageText.TWO_FA_TITLE}</span>
          {showActivateForm &&
            <IconButton
              className="closePosition"
              aria-label="close"
              onClick={hideGenBarcode}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          }
        </div>
        <div className="subDesc">
          <span className="textSteps"><span className="textStepTitle" >Step 1</span>: Install an Authenticator app (Please see the list of all<span className="high" onClick={openInNewTab}> compatible applications</span>, which
            include Google Authenticator
            and Microsoft Authenticator) on your cell phone or computer.</span>
          <span className="textSteps"><span className="textStepTitle">Step 2:</span> {MessageText.HOW_TO_SETUP_2FA_STEP_TWO_TEXT}</span>
          <span className="textSteps"><span className="textStepTitle">Step 3:</span> {MessageText.HOW_TO_SETUP_2FA_STEP_THREE_TEXT}</span>
        </div>
        <div className="qrCodeWrapper">
          <div className="qrCode">
            <div>
              <img src={`data:image/png;base64,${qrCodeData.image}`} alt="Barcode" />
            </div>
            <TextField
              className='fullTextSearchBox'
              autoComplete='off'
              value={qrCodeData.uniqueKey}
              disabled={true}
              inputProps={{ spellCheck: 'false' }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <LightTooltip
                      title="Copied !"
                      placement="top"
                      open={open}
                    >
                      <Button variant="contained" className="searchIconButtoon" onClick={() => { handleCopyText(qrCodeData.uniqueKey) }} type="submit">
                        <ContentCopyRoundedIcon className="searchUserIcon" />
                      </Button>
                    </LightTooltip>
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </div>
        </div>
        {qrCodeData && !showActivateForm && <Formik
          initialValues={initial_state_MFA}
          validationSchema={MFASchema}
          onSubmit={activateMFALoader}
        >
          {({ touched, errors, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <div >
                <Field
                  as={TextField}
                  margin="normal"
                  error={touched.code && Boolean(errors.code)}
                  fullWidth
                  label="Two factor Authentication Code*"
                  autoComplete="off"
                  name="code"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  helperText={<ErrorMessage name="code" />}
                />
                <Grid>
                  <div className="loader">
                    {statusIsLoading && <div style={{ paddingTop: "10px" }}>
                      <CircularProgress />
                    </div>}
                    {!statusIsLoading && <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      style={{ textTransform: "none" }}
                      className="submitBtn"
                      sx={{ mt: 3, mb: 2 }}
                    >
                      {MessageText.ACTIVATE_2FA_TEXT}
                    </Button>}
                  </div>
                </Grid>
              </div>
            </Form>
          )}
        </Formik>}
      </div>
      {msgConfig.snackFlag && <div>
        <SnackBarMessage snacBarValue={msgConfig}
        />
      </div>}
      {showAuthPopup && <Authenticator openPopupAuth={showAuthPopup}
        closeCallback={() => {
          setShowAuthPopup(false);
        }}
      />}
    </div>
  );
};
